import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';

import type { HeaderGroup, Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMatch, useSearchParams } from 'react-router';
import { Box, ButtonBase, FormControlLabel, Switch, useTheme } from '@/shared/ui';
import {
  ArrowDownward,
  ArrowUpward,
  FilterList,
  InfoOutlined,
  UnfoldMore
} from '@/shared/ui/icons';

import { captureEvent } from '@/services/analytics';

import { SHOW_SELECTED_URL_PARAM } from '@/config/constants';
import { Z_INDEX } from '@/config/z-index';
import type { CatalogItem } from '@/entities/catalog-item';
import Button from '@/shared/ui/primitives/Button';
import Tooltip from '@/shared/ui/primitives/Tooltip';
import { useAppSelector } from '@/store/hooks';
import { extractExistingParams } from '../../lib/extractExistingParams';
import { FILTER_DRAWER_WIDTH } from './constants';
import TableSearch from './TableSearch/TableSearch';

// TODO(fuzzy-search-tuning): remove this import once tuning params are finalized
import { FuzzySearchSettingsPanel } from '../../fuzzy-search-tuning';
import { useFilter } from '../../state/useFilter';
import CatalogTableRow from './CatalogTableRow/CatalogTableRow';
import DisplaySettingsPopper from './DisplaySettings/DisplaySettings';
import SelectAllButton from './SelectAllButton/SelectAllButton';
import TableFilters from './TableFilters/TableFilters';

const ShowSelectedSwitch = () => {
  const { filters, setFilter } = useFilter();
  const noneSelected = useAppSelector(state => state.symbolSelector.symbols.length === 0);
  return (
    <FormControlLabel
      control={
        <Switch
          checked={filters[SHOW_SELECTED_URL_PARAM]}
          disabled={noneSelected && !filters[SHOW_SELECTED_URL_PARAM]}
          size='medium'
          onChange={() => {
            setFilter(SHOW_SELECTED_URL_PARAM, !filters[SHOW_SELECTED_URL_PARAM]);
          }}
        />
      }
      label='Show selected data'
    />
  );
};

const CatalogTable = ({
  filterOpen,
  setFilterOpen,
  table,
  isForwardCurves,
  expandedData,
  setExpandedData,
  setShowDiscontinued,
  showDiscontinued,
  renderFactsheetButton
}: {
  filterOpen: boolean;
  setFilterOpen: (filterOpen: boolean) => void;
  table: Table<CatalogItem>;
  isForwardCurves: boolean;
  expandedData: boolean;
  setExpandedData: (newValue: boolean) => void;
  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}) => {
  const { palette } = useTheme();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isInDashboard = Boolean(useMatch('/dashboard/*'));
  const [searchParams] = useSearchParams();
  const rows = table.getRowModel().rows;
  const { isAnyFilterApplied } = useFilter();

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- TanStack Virtual powers catalog row windowing.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 23,
    getScrollElement: () => tableContainerRef.current,
    overscan: 5
  });

  useEffect(() => {
    captureEvent('catalogue_filter', extractExistingParams(searchParams));
  }, [searchParams]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mb: 1
        }}
      >
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
          <Button
            size='small'
            sx={{ width: FILTER_DRAWER_WIDTH - 32, mx: 2, height: 33 }}
            variant='outlined'
            onClick={() => {
              setFilterOpen(!filterOpen);
            }}
          >
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
              <FilterList sx={{ mr: 0.6 }} />
              {isAnyFilterApplied ? (
                <>
                  Filters Applied
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: 'gx.highlight',
                      mx: 1
                    }}
                  />
                </>
              ) : (
                'Filter'
              )}
            </Box>
          </Button>
          <DisplaySettingsPopper
            expandedData={expandedData}
            isForwardCurves={isForwardCurves}
            setExpandedData={setExpandedData}
            setShowDiscontinued={setShowDiscontinued}
            showDiscontinued={showDiscontinued}
            table={table}
          />
          {/* TODO(fuzzy-search-tuning): remove once tuning params are finalized */}
          <FuzzySearchSettingsPanel />
          <ShowSelectedSwitch />
          <SelectAllButton
            isForwardCurves={isForwardCurves}
            table={table}
          />
        </Box>
        <TableSearch tabId={`catalog-search-${isForwardCurves ? 'forward-curves' : 'spot'}`} />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <TableFilters
          expandedData={expandedData}
          filterOpen={filterOpen}
          height='100%'
          setFilterOpen={setFilterOpen}
          table={table}
        />
        <Box
          sx={{
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            ml: filterOpen ? 0 : `${-FILTER_DRAWER_WIDTH}px`,
            transition: 'marginLeft 0.5s ease'
          }}
        >
          {rows.length ? (
            <Box
              ref={tableContainerRef}
              sx={{
                height: '100%',
                overflowY: 'auto',
                position: 'relative',
                zIndex: Z_INDEX.sticky
              }}
            >
              <Box sx={{ display: 'grid' }}>
                <Box
                  sx={{
                    display: 'grid',
                    position: 'sticky',
                    top: 0,
                    zIndex: Z_INDEX.raised,
                    background: palette.gx.tileDark
                  }}
                >
                  {table.getHeaderGroups().map((headerGroup: HeaderGroup<CatalogItem>) => (
                    <Box
                      key={headerGroup.id}
                      sx={{ display: 'flex', padding: '8px', paddingLeft: '16px' }}
                    >
                      {headerGroup.headers.map(header => (
                        <Box
                          key={header.id}
                          sx={{
                            display: 'flex',
                            width: header.getSize(),
                            paddingBottom: '0',
                            paddingRight: '8px',
                            justifyContent: ['Last', 'Change'].includes(
                              header.column.columnDef.header as string
                            )
                              ? 'flex-end'
                              : 'flex-start'
                          }}
                        >
                          <ButtonBase
                            disableRipple
                            sx={{
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              border: 'none',
                              color: 'inherit',
                              background: 'transparent',
                              fontFamily: 'inherit',
                              padding: 0,
                              margin: 0,
                              fontWeight: 700,
                              fontSize: '16px'
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {header.column.columnDef.header !== 'AdditionalItem' &&
                              flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.columnDef.header === 'Info' && (
                              <Tooltip title='Index information pages provide details of the methodology behind the index.'>
                                <InfoOutlined sx={{ ml: 0.5, fontSize: 12 }} />
                              </Tooltip>
                            )}
                            {(header.column.columnDef.header !== 'Info' &&
                              header.column.columnDef.header !== 'AdditionalItem' &&
                              header.column.id !== 'Symbol' &&
                              {
                                asc: (
                                  <ArrowUpward
                                    fontSize='small'
                                    sx={{ ml: 0.5 }}
                                  />
                                ),
                                desc: (
                                  <ArrowDownward
                                    fontSize='small'
                                    sx={{ ml: 0.5 }}
                                  />
                                )
                              }[header.column.getIsSorted() as string]) ?? (
                              <UnfoldMore
                                fontSize='small'
                                sx={{ ml: 0.5 }}
                              />
                            )}
                          </ButtonBase>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
                <Box sx={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                  {rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const row = rows[virtualRow.index];
                    return (
                      <CatalogTableRow
                        key={virtualRow.key}
                        isForwardCurves={isForwardCurves}
                        isInDashboard={isInDashboard}
                        renderFactsheetButton={renderFactsheetButton}
                        row={row}
                        virtualRow={virtualRow}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              Nothing to display here. Try changing your filters.
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CatalogTable;
